import { useMemo } from "react"

import { api } from "~/utils/api"
import {
  knownFlagRows,
  type DirectoryUser,
  type FlagOverrideRow,
} from "~/features/feature-flags/shared/contracts"
import {
  createFlagPanel,
  type DirectoryCompany,
  type FlagPanel,
} from "~/features/feature-flags/client/model/flagPanel"
import { createFlagSnapshot } from "~/features/feature-flags/shared/snapshot"

const DIRECTORY_STALE_MS = 5 * 60_000

const NO_OVERRIDES: FlagOverrideRow[] = []
const NO_USERS: DirectoryUser[] = []

export type FlagPanelData =
  | { status: "pending" }
  | { status: "error"; error: { message: string } }
  | { status: "ready"; panel: FlagPanel }

/** The flag panel's data, fetched through React Query; ready once the environment is known.
 * With `enabled: false` nothing is fetched. `directory: false` skips the user directory, for
 * callers that only need flag values and companies (user names fall back to ids). */
export function useFlagPanelData({
  enabled = true,
  directory: withDirectory = true,
}: { enabled?: boolean; directory?: boolean } = {}): FlagPanelData {
  const overview = api.featureFlagsPanel.overview.useQuery(undefined, { enabled })
  const directory = api.featureFlagsPanel.directory.useQuery(undefined, {
    staleTime: DIRECTORY_STALE_MS,
    enabled: enabled && withDirectory,
  })
  const organizations = api.auth0.getAllOrganizations.useQuery(undefined, {
    staleTime: DIRECTORY_STALE_MS,
    enabled,
  })

  // Rows of flags this build doesn't know (removed, or added by a newer deploy) are dropped here,
  // so everything downstream holds registry keys.
  const overrides = useMemo(
    () => (overview.data ? knownFlagRows(overview.data.overrides) : NO_OVERRIDES),
    [overview.data],
  )

  const companies = useMemo<DirectoryCompany[]>(
    () =>
      (organizations.data ?? [])
        .map((organization) => ({
          id: organization.id,
          name: organization.display_name || organization.name,
          slug: organization.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [organizations.data],
  )

  const environment = overview.data?.environment
  const snapshot = useMemo(
    () => (environment ? createFlagSnapshot(overrides, environment) : null),
    [overrides, environment],
  )

  const customFlagCompanies = overview.data?.customFlagCompanies
  const launchDarklyLock = overview.data?.launchDarklyLock
  const lock = useMemo(
    () =>
      launchDarklyLock && {
        switchOn: launchDarklyLock.switchOn,
        launchDarklyKeys: new Set(launchDarklyLock.launchDarklyKeys),
      },
    [launchDarklyLock],
  )
  const users = directory.data ?? NO_USERS
  const loadingCompanies = organizations.isLoading
  const loadingUsers = directory.isLoading
  const error = overview.error
  return useMemo((): FlagPanelData => {
    if (snapshot && customFlagCompanies && lock) {
      const panel = createFlagPanel(
        snapshot,
        overrides,
        customFlagCompanies,
        { companies, users, loadingCompanies, loadingUsers },
        lock,
      )
      return { status: "ready", panel }
    }
    return error ? { status: "error", error } : { status: "pending" }
  }, [
    snapshot,
    overrides,
    customFlagCompanies,
    lock,
    companies,
    users,
    loadingCompanies,
    loadingUsers,
    error,
  ])
}

/** Refreshes the panel's data after a change. */
export function useInvalidateFlagPanel() {
  const utils = api.useUtils()
  return () =>
    Promise.all([
      utils.featureFlagsPanel.overview.invalidate(),
      utils.featureFlagsPanel.history.invalidate(),
      utils.featureFlags.resolved.invalidate(),
    ])
}
